import type { EDICancellationReason_Top } from 'gw-edge-client/dist/types/dto/pc/typekey/edicancellationreason_top.typekey';
import type { ExternaleCaseDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/policyjob/edi/dto';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { InsuranceCompanyDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/insurancecompany/dto';
import type { OptionProps } from '@flos/react-ui/build/core/forms/select/select-input';
import {
    Grid,
    GridItem,
    SelectField,
    RadioField,
    RadioInput,
    CheckboxField,
    Message,
    DateField,
    TextField
} from '@flos/react-ui';
import StringParagraph from './StringParagraph';
import { useRecoilState, useRecoilValue } from 'recoil';
import StringTitle from './StringTitle';
import {
    addMonths,
    convertDateToLocalDateDTO,
    convertLocalDateDTOToDate,
    convertLocalDateToString,
    getAsapCancelDate,
    getNextMonthsFirstDates
} from '../../../utils/dateUtil';
import { externalCasesAtom } from '../../../recoil/atoms/externalCasesAtom';
import {
    CancellationReason,
    isModifiableCancellationReason,
    updateExternalCaseDTOProperties,
    dateHasValidationErrors,
    isAgricultureLob,
    shouldRenderEdiRadioButtons,
    shouldRenderPrhDatePicker
} from '../../../utils/externalCasesUtil';
import { EDICancellationCoveragesLaterInForce } from './EDICancellationCoveragesLaterinForce';
import EDIValidationErrorMessage from './EDIValidationErrorMessage';
import { useState } from 'react';
import { useHippoText } from '../../../hooks/useHippoText';
import { sortedCoverablesListSelector } from '../../../recoil/selectors/sortedCoverablesListSelector';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';
import PrhEdiCancellationReason from './PrhEdiCancellationReason';
import { shouldShowConditionReportWarning } from '../../../utils/coverablesUtil';
import type { LobStatusFieldsValidation } from '../steps/DatesAndCancellationStep';

const STANDARD_MINUTES_TOP_OFFSET = 1;

const EDICancellationYes = ({
    externalCase,
    coverable,
    companiesSelection,
    lobStatusFieldValidation
}: {
    externalCase: ExternaleCaseDTO;
    coverable: CoverableDTO;
    companiesSelection: OptionProps[];
    lobStatusFieldValidation: LobStatusFieldsValidation;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const {
        cancellationDate,
        insuredIsAccountHolder,
        otherCompany,
        caseId,
        cancellationReason,
        additionalPolicyholder
    } = externalCase;
    const [externalCaseDTOList, setExternalCaseDTOList] = useRecoilState(externalCasesAtom);
    const chosenCancellationDate = cancellationDate
        ? convertLocalDateDTOToDate(cancellationDate)
        : undefined;
    const { sortedCoverables } = useRecoilValue(sortedCoverablesListSelector);
    const [accountHolderData] = useRecoilState(accountHolderAtom);
    const { isCVR } = accountHolderData;
    const isAgriculture = isAgricultureLob(externalCase.lobCode ?? '');
    const asapDate = getAsapCancelDate(isAgriculture);
    const [showErrorMessage, setShowErrorMessage] = useState(
        dateHasValidationErrors(
            convertDateToLocalDateDTO(chosenCancellationDate ?? asapDate),
            coverable,
            sortedCoverables,
            false
        )
    );
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState({
        companyInformation: false,
        cancellationDate: false
    });
    if (!caseId) return <></>;

    const getEdiTitle = (
        isCVR: boolean | undefined,
        flavourName: string | undefined,
        cancellationReason: string | undefined
    ): string => {
        if (isCVR) {
            return getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-COMPANY-SUBHEADER-CVR');
        }
        if (flavourName === 'Husforsikring' || flavourName === 'Fritidshusforsikring') {
            return getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-POLICY-REASON-TITLE');
        }
        return getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-COMPANY-SUBHEADER');
    };

    const getDateTitle = (
        isCVR: boolean | undefined,
        flavourName: string | undefined,
        cancellationReason: string | undefined
    ): string => {
        if (isCVR) {
            return getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-DATE-SUBHEADER-CVR');
        }
        if (
            (flavourName === 'Husforsikring' || flavourName === 'Fritidshusforsikring') &&
            cancellationReason === CancellationReason.ChangeOfOwnership
        ) {
            return getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-POLICY-DATE-TITLE');
        }
        return getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-DATE-SUBHEADER');
    };

    const expirationDateSelection: OptionProps[] = getNextMonthsFirstDates(
        isAgriculture,
        !!isCVR
    ).map((date) => ({
        label: convertLocalDateToString(date),
        value: date.toDateString()
    })) as OptionProps[];

    const isPolicyHolder = (): boolean => insuredIsAccountHolder ?? true;

    const onCompanyChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedIndex = event.target.selectedIndex;
        const { label, value } = event.target.options[selectedIndex];
        const updatedCompany: InsuranceCompanyDTO = {
            name: label,
            code: value
        };
        const updatedExternalCaseProperties = updateExternalCaseDTOProperties(
            externalCaseDTOList,
            [caseId],
            {
                otherCompany: updatedCompany
            }
        );
        setExternalCaseDTOList(updatedExternalCaseProperties);
        setHasUnsavedChanges({ ...hasUnsavedChanges, companyInformation: true });
    };

    const onCancellationDateChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedIndex = event.target.selectedIndex;
        const { value } = event.target.options[selectedIndex];
        const selectedDate = new Date(value);
        const selectedDateDTO = convertDateToLocalDateDTO(selectedDate);
        const updatedExternalCaseProperties = updateExternalCaseDTOProperties(
            externalCaseDTOList,
            [caseId],
            {
                cancellationDate: selectedDateDTO
            }
        );
        setExternalCaseDTOList(updatedExternalCaseProperties);
        setShowErrorMessage(
            dateHasValidationErrors(selectedDateDTO, coverable, sortedCoverables, false)
        );
        setHasUnsavedChanges({ ...hasUnsavedChanges, cancellationDate: true });
    };

    const onCancellationDateChangePrh = (date: Date): void => {
        const selectedDateDTO = convertDateToLocalDateDTO(date);
        // Set minutes_Top to constant due to PC date's minutes_Top is set to that number, without this line, date comparison will be faulty
        selectedDateDTO.minutes_Top = STANDARD_MINUTES_TOP_OFFSET;
        const updatedExternalCaseProperties = updateExternalCaseDTOProperties(
            externalCaseDTOList,
            [caseId],
            {
                cancellationDate: selectedDateDTO
            }
        );
        setExternalCaseDTOList(updatedExternalCaseProperties);
        setShowErrorMessage(
            dateHasValidationErrors(
                convertDateToLocalDateDTO(date),
                coverable,
                sortedCoverables,
                false
            )
        );
        setHasUnsavedChanges({ ...hasUnsavedChanges, cancellationDate: true });
    };

    const setIsPolicyHolder = (choice: boolean): void => {
        const updatedExternalCaseProperties = updateExternalCaseDTOProperties(
            externalCaseDTOList,
            [caseId],
            {
                insuredIsAccountHolder: choice
            }
        );
        setExternalCaseDTOList(updatedExternalCaseProperties);
    };

    const onCancellationReasonChange = (selectedReason: EDICancellationReason_Top): void => {
        const cancelDate = asapDate;
        const cancelDateDTO = convertDateToLocalDateDTO(cancelDate);
        const updateValue: ExternaleCaseDTO = {
            cancellationDate: cancelDate ? cancelDateDTO : undefined,
            cancellationReason: selectedReason,
            cancellationOnExpirationDate: selectedReason === CancellationReason.ExpirationDate
        };
        const updatedExternalCaseProperties = updateExternalCaseDTOProperties(
            externalCaseDTOList,
            [caseId],
            updateValue
        );
        setExternalCaseDTOList(updatedExternalCaseProperties);
        setShowErrorMessage(
            dateHasValidationErrors(cancelDateDTO, coverable, sortedCoverables, false)
        );
    };

    const getExpirationDateLabel = (cancellationReason: string | undefined): string => {
        if (isModifiableCancellationReason(cancellationReason, coverable.flavourName)) {
            return isCVR
                ? getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-DATE-PICKER-CVR')
                : getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-DATE-PICKER');
        }
        return 'Du er forsikret fra';
    };

    const showPolicyHolderCheckbox = (): boolean => {
        switch (true) {
            case coverable.flavourName === 'Husforsikring':
            case coverable.flavourName === 'Fritidshusforsikring':
                return cancellationReason !== CancellationReason.ChangeOfOwnership;
            case isCVR:
                return false;
            default:
                return true;
        }
    };

    return (
        <Grid gap={5}>
            <GridItem span={12} className="u-spacing-stack-top-5">
                <StringTitle space={true}>
                    {getEdiTitle(isCVR, coverable.flavourName, cancellationReason)}
                </StringTitle>
                {(coverable.flavourName === 'Husforsikring' ||
                    coverable.flavourName === 'Fritidshusforsikring') && (
                    <PrhEdiCancellationReason externalCase={externalCase} />
                )}
                <SelectField
                    label={getHippoText(
                        'DSS-EFFECTIVE-DATE-AND-CANCELLATION-INSURANCE-COMPANY-DROPDOWN-LIST'
                    )}
                    id={`yes-othercompany-${String(caseId)}`}
                    onChange={onCompanyChange}
                    value={otherCompany?.code}
                    options={companiesSelection}
                    placeholder="Vælg selskab"
                    disabled={
                        !isModifiableCancellationReason(cancellationReason, coverable.flavourName)
                    }
                    isValid={
                        lobStatusFieldValidation?.isOtherCompanyValid === undefined ||
                        lobStatusFieldValidation.isOtherCompanyValid ||
                        hasUnsavedChanges.companyInformation
                    }
                />
                {additionalPolicyholder?.contactName ? (
                    <TextField
                        label={getHippoText(
                            'DSS-EFFECTIVE-DATE-AND-CANCELLATION-SECONDARY-INSURED'
                        )}
                        value={additionalPolicyholder.contactName}
                        disabled
                    />
                ) : (
                    showPolicyHolderCheckbox() && (
                        <CheckboxField
                            id={`is-account-holder-${String(caseId)}`}
                            label={getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-CHECKMARK')}
                            checked={isPolicyHolder()}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const isChecked = event.target.checked;
                                setIsPolicyHolder(isChecked);
                            }}
                            disabled={
                                !isModifiableCancellationReason(
                                    cancellationReason,
                                    coverable.flavourName
                                )
                            }
                        />
                    )
                )}
            </GridItem>
            {isPolicyHolder() && (
                <GridItem span={12}>
                    <StringTitle space={true}>
                        {getDateTitle(isCVR, coverable.flavourName, cancellationReason)}
                    </StringTitle>
                    {shouldRenderEdiRadioButtons(
                        cancellationReason,
                        coverable.flavourName,
                        isCVR
                    ) && (
                        <RadioField
                            className={`yes-cancelchoice`}
                            key={caseId}
                            value={cancellationReason as string}
                            onChange={(event) => {
                                onCancellationReasonChange(
                                    event.target.value as EDICancellationReason_Top
                                );
                            }}
                        >
                            <RadioInput
                                value={CancellationReason.ShortRate}
                                id={`yes-cancelchoice-earliest-${String(caseId)}`}
                                key={`yes-cancelchoice-earliest-${String(caseId)}`}
                                label={getHippoText(
                                    'DSS-EFFECTIVE-DATE-AND-CANCELLATION-AS-SOON-AS-POSSIBLE-RADIO-BUTTON'
                                )}
                                description={convertLocalDateToString(asapDate)}
                            />
                            <RadioInput
                                value={CancellationReason.ExpirationDate}
                                id={`yes-cancelchoice-expired-${String(caseId)}`}
                                key={`yes-cancelchoice-expired-${String(caseId)}`}
                                label={getHippoText(
                                    'DSS-EFFECTIVE-DATE-AND-CANCELLATION-RENEWAL-DATE-RADIO-BUTTON'
                                )}
                                description={getHippoText(
                                    'DSS-EFFECTIVE-DATE-AND-CANCELLATION-TEXT-EXPLAINER'
                                )}
                            />
                        </RadioField>
                    )}

                    {isCVR && (
                        <>
                            <StringParagraph className="paragraph--medium" space>
                                {getHippoText(
                                    'DSS-EFFECTIVE-DATE-AND-CANCELLATION-DATE-CVR-CHOOSE-RENEWAL-DAY-1'
                                )}
                            </StringParagraph>
                            <StringParagraph className="paragraph--medium" space>
                                {getHippoText(
                                    'DSS-EFFECTIVE-DATE-AND-CANCELLATION-DATE-CVR-CHOOSE-RENEWAL-DAY-2'
                                )}
                            </StringParagraph>
                        </>
                    )}

                    {(cancellationReason === CancellationReason.ExpirationDate ||
                        !isModifiableCancellationReason(
                            cancellationReason,
                            coverable.flavourName
                        )) && (
                        <SelectField
                            id={`yes-datefield-${String(caseId)}`}
                            placeholder={
                                isModifiableCancellationReason(
                                    cancellationReason,
                                    coverable.flavourName
                                )
                                    ? 'Vælg...'
                                    : convertLocalDateToString(chosenCancellationDate)
                            }
                            label={getExpirationDateLabel(cancellationReason)}
                            value={
                                chosenCancellationDate
                                    ? chosenCancellationDate.toDateString()
                                    : asapDate.toDateString()
                            }
                            onChange={onCancellationDateChange}
                            options={expirationDateSelection}
                            disabled={
                                !isModifiableCancellationReason(
                                    cancellationReason,
                                    coverable.flavourName
                                )
                            }
                            isValid={
                                lobStatusFieldValidation?.isCancellationDateValid === undefined ||
                                lobStatusFieldValidation.isCancellationDateValid ||
                                hasUnsavedChanges.cancellationDate
                            }
                        />
                    )}
                    {shouldRenderPrhDatePicker(cancellationReason, coverable.flavourName) && (
                        <DateField
                            label={getHippoText(
                                'DSS-EFFECTIVE-DATE-AND-CANCELLATION-POLICY-CHOOSE-DATE'
                            )}
                            onChange={(date: Date): void => {
                                onCancellationDateChangePrh(date);
                            }}
                            minDate={new Date()}
                            maxDate={addMonths(new Date(), 12)}
                            value={
                                chosenCancellationDate
                                    ? chosenCancellationDate.toDateString()
                                    : undefined
                            }
                            isValid={
                                lobStatusFieldValidation?.isCancellationDateValid === undefined ||
                                lobStatusFieldValidation.isCancellationDateValid ||
                                hasUnsavedChanges.cancellationDate
                            }
                        />
                    )}
                    {shouldShowConditionReportWarning(coverable) && (
                        <Message theme={'warning'} className="u-spacing-stack-m">
                            {getHippoText(
                                'DSS-EFFECTIVE-DATE-AND-CANCELLATION-WARNING-CONDITION-REPORT'
                            )}
                        </Message>
                    )}
                    <div id="earlist-choice-warning">
                        {shouldRenderEdiRadioButtons(
                            cancellationReason,
                            coverable.flavourName,
                            isCVR
                        ) && (
                            <StringParagraph
                                small
                                className="u-spacing-stack-s u-color-secondary-4-medium"
                            >
                                {getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-TEXT')}
                            </StringParagraph>
                        )}
                        {isCVR && (
                            <Message theme={'success'}>
                                {getHippoText(
                                    'DSS-EFFECTIVE-DATE-AND-CANCELLATION-DATE-CVR-MANUAL-RENEWAL-MESSAGE'
                                )}
                            </Message>
                        )}
                    </div>
                    <EDICancellationCoveragesLaterInForce
                        coverable={coverable}
                        cancellationDate={cancellationDate}
                    />
                </GridItem>
            )}
            {showErrorMessage && (
                <GridItem span={12}>
                    <EDIValidationErrorMessage coverable={coverable} />
                </GridItem>
            )}
        </Grid>
    );
};

export default EDICancellationYes;
